import { useState, useRef } from "react";
import { X, Camera, Loader2 } from "lucide-react";
import { updateProfile } from "../../APIs/ProfileApi";

const EditProfileModal = ({ profile, onClose, onUpdate }) => {
  const [fullName, setFullName] = useState(profile.full_name || "");
  const [bio, setBio] = useState(profile.bio || "");
  const [website, setWebsite] = useState(profile.website || "");
  const [avatar, setAvatar] = useState(null);
  const [banner, setBanner] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(profile.avatar_url);
  const [bannerPreview, setBannerPreview] = useState(profile.banner_url);
  const [saving, setSaving] = useState(false);

  const avatarRef = useRef(null);
  const bannerRef = useRef(null);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatar(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBanner(file);
      setBannerPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const form = new FormData();
      form.append("full_name", fullName);
      form.append("bio", bio);
      form.append("website", website);
      if (avatar) form.append("avatar", avatar);
      if (banner) form.append("banner", banner);

      const res = await updateProfile(profile.id, form);
      onUpdate(res.data);
      onClose();
    } catch (err) {
      console.error("Update error:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]
      flex items-end md:items-center md:justify-center">
      <div className="bg-[#111] w-full md:max-w-[480px] rounded-t-3xl
        md:rounded-2xl border-t md:border border-white/10
        max-h-[90vh] overflow-y-auto">

        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-white/20 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3
          border-b border-white/10">
          <h2 className="text-white font-semibold">Edit Profile</h2>
          <button onClick={onClose}
            className="p-1.5 rounded-full hover:bg-white/10 transition">
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">

          {/* Banner */}
          <div
            className="relative h-28 rounded-2xl overflow-hidden cursor-pointer
              bg-white/5 border border-white/10"
            onClick={() => bannerRef.current.click()}
          >
            {bannerPreview && (
              <img src={bannerPreview} alt="banner"
                className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 flex items-center justify-center
              bg-black/30 hover:bg-black/40 transition">
              <Camera className="w-6 h-6 text-white/60" />
            </div>
          </div>

          {/* Avatar */}
          <div className="flex items-center gap-4">
            <div
              className="relative w-16 h-16 rounded-full overflow-hidden
                cursor-pointer bg-white/10 border border-white/10"
              onClick={() => avatarRef.current.click()}
            >
              {avatarPreview && (
                <img src={avatarPreview} alt="avatar"
                  className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 flex items-center justify-center
                bg-black/30 hover:bg-black/40 transition">
                <Camera className="w-4 h-4 text-white/60" />
              </div>
            </div>
            <div>
              <p className="text-white text-sm font-medium">Profile Photo</p>
              <p className="text-white/30 text-xs">Tap to change</p>
            </div>
          </div>

          {/* Fields */}
          {[
            { label: "Full Name", value: fullName, setter: setFullName },
            { label: "Bio", value: bio, setter: setBio },
            { label: "Website", value: website, setter: setWebsite },
          ].map((field) => (
            <div key={field.label}>
              <label className="text-white/40 text-xs uppercase tracking-wider
                mb-1.5 block">
                {field.label}
              </label>
              <input
                value={field.value}
                onChange={(e) => field.setter(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl
                  px-4 py-3 text-white text-sm outline-none
                  placeholder-white/20 focus:border-white/20 transition"
              />
            </div>
          ))}

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={saving}
            className={`w-full py-3 rounded-xl font-medium text-sm
              flex items-center justify-center gap-2 transition mt-2
              ${saving
                ? "bg-white/5 text-white/20 cursor-not-allowed"
                : "bg-white text-black hover:bg-gray-100 active:scale-95"
              }`}
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
            ) : "Save Changes"}
          </button>

          {/* Safe area spacer — prevents button hiding behind mobile footer */}
          <div className="h-4 md:hidden" />

        </div>

        {/* Hidden inputs */}
        <input ref={avatarRef} type="file" accept="image/*"
          className="hidden" onChange={handleAvatarChange} />
        <input ref={bannerRef} type="file" accept="image/*"
          className="hidden" onChange={handleBannerChange} />
      </div>
    </div>
  );
};

export default EditProfileModal;